import type { AdventureDetail } from '../../services/GameApiService';
import AdventureModeBadge from './AdventureModeBadge';
import AdventureSocialProofBadge from './AdventureSocialProofBadge';

interface AdventureDetailHeroProps {
  adventure: AdventureDetail;
}

/**
 * Rótulos PT-PT para o tom da aventura exibido no hero. O backend
 * persiste o tom como chave canónica em inglês (humorous/dramatic/casual/
 * professional) — ver `apps/api/src/services/game/toneConfig.js`. Aqui
 * traduzimos para a etiqueta visível ao jogador. Em caso de chave
 * desconhecida, faz-se fallback para a própria chave (com capitalização).
 */
const TONE_LABELS_PT: Record<string, string> = {
  humorous: 'comédia',
  dramatic: 'drama',
  casual: 'conversa amigável',
  professional: 'interacção profissional',
};

function formatToneLabel(tone: string): string {
  return TONE_LABELS_PT[tone] ?? tone;
}

/**
 * Hero da página de detalhe de uma aventura.
 * Mostra a imagem hero (com fallback para gradiente temático),
 * o tema como label, o título, o nº de episódios e o papel do jogador.
 */
export default function AdventureDetailHero({ adventure }: AdventureDetailHeroProps) {
  return (
    <section className="relative overflow-hidden">
      <div className="absolute inset-0">
        {adventure.heroImageUrl ? (
          <img
            src={adventure.heroImageUrl}
            alt={adventure.title}
            className="w-full h-full object-cover object-top"
          />
        ) : (
          <div className="w-full h-full bg-gradient-to-br from-dark via-slate-800 to-primary" />
        )}
      </div>

      <div className="relative z-10 px-4 pt-44 pb-12 md:pt-72 md:pb-16">
        <div className="max-w-3xl mx-auto text-center">
          {adventure.theme && (
            <p className="text-sm md:text-base font-semibold uppercase tracking-widest text-accent mb-3">
              {adventure.theme}
            </p>
          )}
          {adventure.featured && (
            <div className="inline-block px-3 py-1 mb-3 text-xs font-bold uppercase tracking-wider rounded-full bg-amber-500 text-white shadow-lg">
              Em destaque
            </div>
          )}
          <h1 className="text-3xl md:text-5xl font-bold text-white mb-4 leading-tight">
            {adventure.title}
          </h1>
          <div className="flex flex-wrap items-center justify-center gap-3 text-white/90 text-sm md:text-base">
            <AdventureModeBadge mode={adventure.mysteryMode} variant="pill" />
            {adventure.totalEpisodes != null && (
              <span className="px-3 py-1 rounded-full bg-white/15 backdrop-blur-sm">
                {adventure.totalEpisodes} episódios
              </span>
            )}
            {adventure.playerRole && (
              <span className="px-3 py-1 rounded-full bg-white/15 backdrop-blur-sm">
                És: {adventure.playerRole}
              </span>
            )}
            {adventure.tone && (
              <span className="px-3 py-1 rounded-full bg-white/15 backdrop-blur-sm capitalize">
                {formatToneLabel(adventure.tone)}
              </span>
            )}
          </div>
          <div className="mt-4">
            <AdventureSocialProofBadge
              adventureId={adventure.id}
              completedCount={adventure.completedCount}
              variant="hero"
            />
          </div>
        </div>
      </div>
    </section>
  );
}